/* istanbul ignore file */
/**
 * Copyright Warner Bros. Entertainment, Inc.
 * All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains the property
 * of Warner Bros. Entertainment, Inc. and its suppliers, if any.
 * The intellectual and technical concepts contained herein are
 * proprietary to Warner Bros. Entertainment, Inc. and its suppliers
 * and may be covered by U.S. and Foreign Patents, patents in process,
 * and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material is
 * unlawful and strictly forbidden unless prior written permission is
 * obtained from Warner Bros. Entertainment, Inc.
 */
import {datadogRum} from '@datadog/browser-rum';

const CONSTANTS = {
    ACTIONS: {
        // Please keep any changes/additions documented in confluence: https://wbddigital.atlassian.net/wiki/spaces/STUDIO/pages/123174937/Custom+Actions
        LOGIN: {
            START: 'rum.login.start', // When user clicks login button
            MFA_CHALLENGE_SENT: 'rum.login.mfa-challenge-sent', // When an MFA challenge is sent to user
            MFA_CHALLENGE_COMPLETED: 'rum.login.mfa-challenge-completed', // When user completes MFA challenge
            SUCCESS: 'rum.login.completed', // When user is logged in and redirected to homepage
            ERROR: 'rum.login.error' // When user encounters a login error
        },
        VIDEO_PLAYBACK: {
            START: 'rum.video-playback.start', // When user clicks the play button
            SUCCESS: 'rum.video-playback.success', // When player reports video begins playback
            ERROR: 'rum.video-playback.error', // When user is not able to play a video for any reason
        }
    }
};

// TODO: add a list of views that we consider safe and without PII.
// This should be provided from the config so that each app can have its own list of views.
const SAFE_VIEWS_WITHOUT_PII = [
];
const shouldRedactTargetName = event => {
    return event.type === 'action' &&
        event.action.type !== 'custom' &&
        !SAFE_VIEWS_WITHOUT_PII.some(v => !!event.view.url.match(v));
};

const IGNORE_ERROR_PATTERNS = [
    // Standard React 15 warnings in console.
    /^console error: Warning:/,
    // TinyMCE plugin that sometimes fails to load due to Ad Blockers
    /^console error: Failed to load plugin: paste from url plugins\/paste\/plugin.min.js$/,
    // Bluebird aborted requests are captured this way.
    /Uncaught \\"Empty reason\\"/,
    // Remove Request has been terminated errors
    /Request has been terminated|Possible causes: the network is offline/,
];
const shouldDiscardError = event => {
    return IGNORE_ERROR_PATTERNS.some(pattern => pattern.test(event.error?.message));
};

const IGNORE_XHR_PATTERNS = [
    // API endpoints we want to ignore because they may leak PII or other reasons.
    /\/user\/validation\/password-reset-allowed/, // This endpoint leaks emails in its URL.
];
const shouldDiscardXHR = event => {
    return event.resource?.type === 'xhr' &&
        IGNORE_XHR_PATTERNS.some(pattern => pattern.test(event.resource?.url));
};

/* Datadog client wrapper
    Tracking User Actions: https://docs.datadoghq.com/real_user_monitoring/browser/tracking_user_actions/#send-custom-actions

    addAction: (name: string, context?: object | undefined) => void;
    addError: (error: unknown, context?: object | undefined) => void;
    addTiming: (name: string, time?: number | undefined) => void;
*/
class DatadogClient {
    /**
     * init Params:
     * app: {name, version}
     * config: linked config file
     * getPartner: function that returns the current partner
     */
    init(app, config) {
        // console.log(`*** DatadogClient.init (Environment: ${config.Environment} Enabled: ${!!config.Datadog.enabledForEnvironment})`, {app, config});
        // Prevent initialization of Datadog in non-allowed environments
        if (!config.Datadog.enabledForEnvironment) {
            return;
        }

        datadogRum.init(Object.assign({
            /* NOTE: Required fields in config:
                applicationId: rum application id
                clientToken: rum application client token
                service: tag for reporting (example: sie:brainiac:cms)
            */
            allowedTracingUrls: [config.ApiBaseUrl],
            beforeSend: (event) => {
                if (shouldRedactTargetName(event)) {
                    // Redact data on userInteraction actions, but allow our custom actions to pass through
                    event.action.target.name = 'xxxxxx';
                }

                // Because false means it should discard the event.
                return !(
                    shouldDiscardError(event) ||
                    shouldDiscardXHR(event)
                );
            },
            defaultPrivacyLevel: 'mask',
            env: config.Environment,
            site: 'us3.datadoghq.com',
            sessionReplaySampleRate: 5,
            sessionSampleRate: 50,
            traceSampleRate: 50,
            trackFrustrations: true,
            trackLongTasks: true,
            trackResources: true,
            trackUserInteractions: true,
            version: app.version
        }, config.Datadog));

        datadogRum.startSessionReplayRecording();
    }

    // Send RUM Custom Actions: https://docs.datadoghq.com/real_user_monitoring/guide/send-rum-custom-actions/?tab=npm
    addAction(actionName, context = undefined) {
        datadogRum.addAction(actionName, context);
    }
    // Send a custom Error (see https://docs.datadoghq.com/real_user_monitoring/browser/collecting_browser_errors/?tab=npm#collect-errors-manually)
    addError(err = new Error(), context = undefined) {
        datadogRum.addError(err, context);
    }
}

const client = new DatadogClient();

export {
    client as DatadogClient,
    CONSTANTS as DatadogConstants,
};
